import { useState, useRef, useCallback, useEffect } from "react";
import toast from "react-hot-toast";

import {
  generateAIModel,
  getAIModelHistory,
  saveAIModelToLibrary,
  deleteAIModelHistory,
} from "../../api/aiGenerationService";

export const SAMPLE_MODEL_IMAGES = {
  women: [
    {
      id: "wm-1",
      ethnicity: "Caucasian / European",
      image: "https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&w=1000&q=80",
    },
    {
      id: "wm-2",
      ethnicity: "East Asian",
      image: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=1000&q=80",
    },
    {
      id: "wm-3",
      ethnicity: "African / Black",
      image: "https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?auto=format&fit=crop&w=1000&q=80",
    },
    {
      id: "wm-4",
      ethnicity: "South Asian / Indian",
      image: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=1000&q=80",
    },
    {
      id: "wm-5",
      ethnicity: "Hispanic / Latina",
      image: "https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&w=1000&q=80",
    },
    {
      id: "wm-6",
      ethnicity: "Middle Eastern / Arab",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=1000&q=80",
    },
  ],
  men: [
    {
      id: "mn-1",
      ethnicity: "Caucasian / European",
      image: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&w=1000&q=80",
    },
    {
      id: "mn-2",
      ethnicity: "East Asian",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=1000&q=80",
    },
    {
      id: "mn-3",
      ethnicity: "African / Black",
      image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=1000&q=80",
    },
    {
      id: "mn-4",
      ethnicity: "South Asian / Indian",
      image: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=1000&q=80",
    },
    {
      id: "mn-5",
      ethnicity: "Hispanic / Latino",
      image: "https://images.unsplash.com/photo-1622253692010-333f2da6031d?auto=format&fit=crop&w=1000&q=80",
    },
  ],
};

const POLL_INTERVAL_MS = 7000; // 7 seconds as requested

const mapErrorCodeToMessage = (errorCode, rawError) => {
  switch (errorCode) {
    case "GPU_WORKER_TIMEOUT":
      return "Model generation timed out on GPU server. Please try again.";
    case "GENERATION_FAILED":
      return "Model generation failed. Please refine your attributes and try again.";
    case "SERVER_RESTARTED":
      return "Server restarted during processing. Please resubmit your request.";
    case "INTERNAL_ERROR":
      return "Internal processing error occurred.";
    default:
      return rawError || "Generation failed. Please try again.";
  }
};

const MODEL_STAGES = [
  "Synthesizing...",
  "Rendering model...",
  "Applying lighting...",
  "Finalizing...",
];

const getModelStageText = (status) => {
  if (status === "queued") return "In queue...";
  if (status === "failed") return "Failed";
  if (status === "completed") return "Complete!";
  return MODEL_STAGES[0];
};

const getNextModelStage = (currentStage) => {
  const idx = MODEL_STAGES.indexOf(currentStage);
  if (idx === -1) return MODEL_STAGES[0];
  if (idx < MODEL_STAGES.length - 1) return MODEL_STAGES[idx + 1];
  return MODEL_STAGES[MODEL_STAGES.length - 1];
};

export const useModelGenerator = ({ onAddModelLocally } = {}) => {
  const [jobs, setJobs] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyStats, setHistoryStats] = useState({
    total: 0,
    queued: 0,
    generating: 0,
    completed: 0,
    failed: 0,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });
  const [filters, setFilters] = useState([]);
  const [isFetchingHistory, setIsFetchingHistory] = useState(false);

  const pollTimerRef = useRef(null);
  const smoothProgressIntervalRef = useRef(null);
  const lastClearedRef = useRef([]);
  const lastFilterRef = useRef({ page: 1, limit: 10, status: undefined, search: undefined });

  // Normalize backend model job object for UI rendering
  const normalizeJob = useCallback((j) => {
    const rawId = j.jobId || j.id;
    const promptText =
      j.prompt ||
      j.params?.prompt ||
      j.name ||
      "AI Generated Model";

    return {
      ...j,
      id: rawId,
      jobId: rawId,
      name: j.name || (promptText.length > 40 ? `${promptText.slice(0, 40)}...` : promptText),
      prompt: promptText,
      gender: j.gender || j.params?.gender || "women",
      age: j.age || j.params?.age,
      ethnicity: j.ethnicity || j.params?.ethnicity,
      skinTone: j.skinTone || j.params?.skinTone,
      bodyType: j.bodyType || j.params?.bodyType,
      hair: j.hair || j.params?.hair,
      clothing: j.clothing || j.params?.clothing,
      footwear: j.footwear || j.params?.footwear,
      background: j.background || j.params?.background,
      customPrompt: j.customPrompt || j.params?.customPrompt,
      status: j.status || "queued",
      progress: j.status === "completed" ? 100 : 0,
      stageText:
        j.status === "queued"
          ? "In queue..."
          : j.status === "completed"
          ? "Complete!"
          : j.status === "failed"
          ? "Failed"
          : (j.stageText && j.stageText !== "Initializing...")
          ? j.stageText
          : MODEL_STAGES[0],
      resultUrl: j.resultUrl || (Array.isArray(j.images) && j.images[0]) || null,
      error: j.error ? mapErrorCodeToMessage(j.errorCode, j.error) : null,
      rawError: j.error,
      errorCode: j.errorCode,
      createdAt: j.createdAt || new Date().toISOString(),
      savedToLibrary: Boolean(j.savedToLibrary),
      libraryId: j.libraryId || null,
    };
  }, []);

  // Fetch paginated history from backend
  const fetchHistory = useCallback(
    async ({ page = 1, limit = 10, status, search, silent = false } = {}) => {
      if (!silent) setIsFetchingHistory(true);
      lastFilterRef.current = { page, limit, status, search };
      try {
        const res = await getAIModelHistory({ page, limit, status, search });
        if (res.success && res.data) {
          const { rows = [], total = 0, totalPages = 1, stats = {}, filters: apiFilters = [] } = res.data;
          if (Array.isArray(apiFilters) && apiFilters.length > 0) {
            setFilters(apiFilters);
          }

          const normalizedRows = rows.map(normalizeJob);

          const activeJobs = normalizedRows.filter(
            (j) => j.status === "queued" || j.status === "generating"
          );
          const finishedRows = normalizedRows.filter(
            (j) => j.status === "completed" || j.status === "failed"
          );

          // Preserve in-flight visual progress on active jobs
          setJobs((prevJobs) => {
            if (status && status !== "all" && status !== "generating" && status !== "queued") {
              if (stats.queued === 0 && stats.generating === 0) {
                return [];
              }
              return prevJobs;
            }
            return activeJobs.map((serverJob) => {
              const localMatch = prevJobs.find((p) => p.id === serverJob.id);
              if (localMatch && serverJob.status === "generating") {
                return {
                  ...serverJob,
                  stageText: localMatch.stageText || MODEL_STAGES[0],
                };
              }
              return serverJob;
            });
          });

          setHistory(finishedRows);
          setHistoryStats(stats);
          setPagination({ page, limit, total, totalPages });

          return { success: true, activeJobs, stats };
        }
        return { success: false };
      } catch (err) {
        console.error("Failed to fetch model generation history:", err);
        return { success: false };
      } finally {
        if (!silent) setIsFetchingHistory(false);
      }
    },
    [normalizeJob]
  );

  const fetchHistoryRef = useRef(fetchHistory);
  useEffect(() => {
    fetchHistoryRef.current = fetchHistory;
  });

  // Initial load
  useEffect(() => {
    fetchHistory({ page: 1, limit: 10 });
  }, [fetchHistory]);

  const hasGeneratingJobs = jobs.some((j) => j.status === "generating");
  const hasActiveQueue =
    hasGeneratingJobs ||
    jobs.some((j) => j.status === "queued") ||
    Boolean(historyStats && (historyStats.queued > 0 || historyStats.generating > 0));

  // Interpolated smooth progress simulation for active generating jobs between polls
  useEffect(() => {
    if (hasGeneratingJobs) {
      if (!smoothProgressIntervalRef.current) {
        smoothProgressIntervalRef.current = setInterval(() => {
          setJobs((prevJobs) =>
            prevJobs.map((j) => {
              if (j.status === "generating") {
                const nextStage = getNextModelStage(j.stageText);
                if (nextStage !== j.stageText) {
                  return {
                    ...j,
                    stageText: nextStage,
                  };
                }
              }
              return j;
            })
          );
        }, 4500);
      }
    } else {
      if (smoothProgressIntervalRef.current) {
        clearInterval(smoothProgressIntervalRef.current);
        smoothProgressIntervalRef.current = null;
      }
    }

    return () => {
      if (smoothProgressIntervalRef.current) {
        clearInterval(smoothProgressIntervalRef.current);
        smoothProgressIntervalRef.current = null;
      }
    };
  }, [hasGeneratingJobs]);

  // Master Polling Lifecycle (7-second timer)
  useEffect(() => {
    if (!hasActiveQueue) {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      return;
    }

    if (!pollTimerRef.current) {
      pollTimerRef.current = setInterval(async () => {
        const result = await fetchHistoryRef.current({
          ...lastFilterRef.current,
          silent: true,
        });

        if (result?.success) {
          const stillActive =
            (result.activeJobs && result.activeJobs.length > 0) ||
            Boolean(result.stats && (result.stats.queued > 0 || result.stats.generating > 0));

          if (!stillActive && pollTimerRef.current) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
          }
        }
      }, POLL_INTERVAL_MS);
    }

    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [hasActiveQueue]);

  // Submit model generation job to backend
  const startGeneration = useCallback(
    async ({
      name,
      gender = "women",
      age,
      ethnicity,
      skinTone,
      bodyType,
      hair,
      hairType,
      hairLength,
      hairColor,
      clothing,
      footwear,
      background,
      customPrompt,
      ...rest
    }) => {
      let parsedAge = age;
      if (typeof age === "string") {
        const match = age.match(/\d+/);
        if (match) parsedAge = parseInt(match[0], 10);
      } else if (typeof age === "number") {
        parsedAge = age;
      }

      const hairObj =
        typeof hair === "object" && hair !== null
          ? hair
          : {
              type: hairType || "Straight",
              length: hairLength || "Medium",
              color: hairColor || "Brown",
            };

      const payload = {
        name: name?.trim(),
        gender: gender === "male" || gender === "men" ? "men" : "women",
      };

      if (parsedAge) payload.age = parsedAge;
      if (ethnicity) payload.ethnicity = ethnicity.trim();
      if (skinTone) payload.skinTone = skinTone.trim();
      if (bodyType) payload.bodyType = bodyType.trim();
      if (hairObj) payload.hair = hairObj;
      if (clothing) payload.clothing = clothing.trim();
      if (footwear) payload.footwear = footwear.trim();
      if (background) payload.background = background.trim();
      if (customPrompt) payload.customPrompt = customPrompt.trim();

      const toastId = toast.loading("Submitting model generation...");

      const res = await generateAIModel(payload);

      if (!res.success) {
        toast.error(res.message || "Failed to start model generation", { id: toastId });
        return { success: false, message: res.message, status: res.status };
      }

      const { jobId, status = "generating" } = res.data;

      const newJob = normalizeJob({
        jobId,
        id: jobId,
        name: payload.name,
        gender: payload.gender,
        age: payload.age,
        ethnicity: payload.ethnicity,
        skinTone: payload.skinTone,
        bodyType: payload.bodyType,
        hair: payload.hair,
        clothing: payload.clothing,
        footwear: payload.footwear,
        background: payload.background,
        customPrompt: payload.customPrompt,
        status,
        progress: 0,
        stageText:
          status === "queued"
            ? "In queue..."
            : "Synthesizing...",
        createdAt: new Date().toISOString(),
        params: payload,
      });

      setJobs((prev) => [newJob, ...prev.filter((j) => j.id !== jobId)]);

      toast.success(
        status === "queued"
          ? "Generation queued! Position tracking above."
          : "Generation started! Synthesizing fashion model...",
        { id: toastId, icon: "✨" }
      );

      // Trigger immediate background sync to kick off polling loop
      fetchHistory({ page: 1, limit: pagination.limit, silent: true });

      return { success: true, jobId, status };
    },
    [normalizeJob, pagination.limit, fetchHistory]
  );

  // Retry failed job
  const retryJob = useCallback(
    (jobId) => {
      const job =
        jobs.find((j) => j.id === jobId) ||
        history.find((h) => h.id === jobId);

      if (!job) return;

      const retryParams = job.params || {
        name: job.name,
        gender: job.gender,
        age: job.age,
        ethnicity: job.ethnicity,
        skinTone: job.skinTone,
        bodyType: job.bodyType,
        hair: job.hair,
        clothing: job.clothing,
        footwear: job.footwear,
        background: job.background,
        customPrompt: job.customPrompt,
      };

      startGeneration(retryParams);
    },
    [jobs, history, startGeneration]
  );

  // Cancel / dismiss active job card from client view
  const cancelJob = useCallback((jobId) => {
    setJobs((prev) => prev.filter((j) => j.id !== jobId));
    toast("Job removed from active view.", { icon: "⏹️" });
  }, []);

  const dismissJob = useCallback((jobId) => {
    setJobs((prev) => prev.filter((j) => j.id !== jobId));
  }, []);

  // Save completed AI model to store library
  const saveToLibrary = useCallback(
    async (jobOrHistoryItem) => {
      if (!jobOrHistoryItem) return;
      const targetId = jobOrHistoryItem.id || jobOrHistoryItem.jobId;

      const toastId = toast.loading("Saving model to library...");

      const res = await saveAIModelToLibrary(targetId, {
        name: jobOrHistoryItem.name,
        gender: jobOrHistoryItem.gender === "men" ? "men" : "women",
        tier: "AI Studio",
        bIsActive: true,
      });

      if (!res.success) {
        toast.error(res.message || "Failed to save to library", { id: toastId });
        return { success: false, message: res.message };
      }

      const savedData = res.data || {};

      // Notify parent listing hook if provided to refresh catalog immediately
      if (onAddModelLocally) {
        onAddModelLocally({
          id: savedData.id,
          name: savedData.name || jobOrHistoryItem.name,
          title: savedData.name || jobOrHistoryItem.name,
          front: savedData.front || jobOrHistoryItem.resultUrl,
          image: savedData.front || jobOrHistoryItem.resultUrl,
          images: [savedData.front || jobOrHistoryItem.resultUrl],
          gender: savedData.gender || jobOrHistoryItem.gender,
          category: savedData.gender || jobOrHistoryItem.gender,
          tier: "AI Studio",
          source: "ai",
          modelType: "model",
          bIsActive: 1,
          isActive: 1,
        });
      }

      // Mark saved in local history state
      setHistory((prev) =>
        prev.map((h) =>
          h.id === targetId
            ? { ...h, savedToLibrary: true, libraryId: savedData.id }
            : h
        )
      );

      // Dismiss from live queue if still present
      setJobs((prev) => prev.filter((j) => j.id !== targetId));

      toast.success("Saved to your model library!", {
        id: toastId,
        icon: "🎉",
      });

      return { success: true, data: savedData };
    },
    [onAddModelLocally]
  );

  // Delete history item
  const deleteHistoryItem = useCallback(
    async (id) => {
      const targetId = typeof id === "object" && id !== null ? (id.id || id.jobId) : id;

      const targetJob = jobs.find((j) => j.id === targetId);
      if (targetJob && targetJob.status === "generating") {
        toast.error("Active generation cannot be deleted while in progress.");
        return;
      }

      // Identify the item being deleted to know its status
      const itemToDelete = history.find((h) => h.id === targetId) || targetJob;
      const deletedStatus = itemToDelete?.status || "completed";

      const toastId = toast.loading("Deleting generation draft...");
      const res = await deleteAIModelHistory(targetId);

      if (!res.success) {
        toast.error(res.message || "Failed to delete item", { id: toastId });
        return;
      }

      // Optimistically update history and jobs lists
      setHistory((prev) => prev.filter((item) => item.id !== targetId));
      setJobs((prev) => prev.filter((item) => item.id !== targetId));

      // Optimistically update stat counts for All, Completed, and other tabs
      setHistoryStats((prev) => {
        if (!prev) return prev;
        const statusKey =
          deletedStatus && prev[deletedStatus] !== undefined
            ? deletedStatus
            : "completed";
        return {
          ...prev,
          total: Math.max(0, (prev.total || 0) - 1),
          [statusKey]: Math.max(0, (prev[statusKey] || 0) - 1),
        };
      });

      // Optimistically update pagination total and totalPages
      setPagination((prev) => {
        if (!prev) return prev;
        const newTotal = Math.max(0, (prev.total || 0) - 1);
        const limit = prev.limit || 10;
        const newTotalPages = Math.max(1, Math.ceil(newTotal / limit));
        const newPage = Math.min(prev.page || 1, newTotalPages);
        return {
          ...prev,
          page: newPage,
          total: newTotal,
          totalPages: newTotalPages,
        };
      });

      // Re-fetch to synchronize canonical data and stats from server without UI flicker
      const currentFilter = lastFilterRef.current || {};
      const currentPage = currentFilter.page || pagination.page || 1;
      const currentLimit = currentFilter.limit || pagination.limit || 10;
      const remainingOnPage = history.filter((item) => item.id !== targetId);
      const targetPage =
        remainingOnPage.length === 0 && currentPage > 1 ? currentPage - 1 : currentPage;

      fetchHistory({
        ...currentFilter,
        page: targetPage,
        limit: currentLimit,
        silent: true,
      });

      toast.success("Draft removed from history", { id: toastId });
    },
    [jobs, history, pagination, fetchHistory]
  );

  const clearAllFinished = useCallback(() => {
    const finishedJobs = jobs.filter((j) => j.status !== "generating");
    if (finishedJobs.length === 0) return;

    lastClearedRef.current = finishedJobs;
    setJobs((prev) => prev.filter((j) => j.status === "generating"));

    toast("Active queue drafts cleared.", { icon: "📁" });
  }, [jobs]);

  const undoClear = useCallback(() => {
    if (lastClearedRef.current.length > 0) {
      setJobs((prev) => [...lastClearedRef.current, ...prev]);
      lastClearedRef.current = [];
      toast.success("Restored drafts to active queue!");
    }
  }, []);

  return {
    // Aliases to support all consumer patterns
    genJobs: jobs,
    jobs,
    genHistory: history,
    history,
    historyStats,
    pagination,
    filters,
    isFetchingHistory,
    fetchHistory,
    startGeneration,
    retryJob,
    cancelJob,
    dismissJob,
    saveToLibrary,
    deleteHistoryItem,
    clearAllFinished,
    undoClear,
    hasClearedDrafts: lastClearedRef.current.length > 0,
  };
};
